'use client'
import { useRef, useEffect } from 'react'
import { PlusIcon, ChevronDownIcon } from '@heroicons/react/16/solid'
import { JosefButton } from '../buttons'
import { JosefIconButton } from '../buttons'
import { JosefInput } from '../input'
import { cn } from '../../helpers/classnames'
import { NativeDialog, type NativeDialogHandle } from '../../native-dialog'

export interface EmailAddress {
  id: string
  name: string
  email: string
  verification_status?: string
}

interface JosefSenderSelectorProps {
  label?: string
  placeholder?: string
  value?: string
  emailAddresses: EmailAddress[]
  isLoading?: boolean
  onChange: (addressId: string, address: EmailAddress | undefined) => void
  onCreateSender?: (data: { name: string; email: string }) => Promise<EmailAddress>
  className?: string
  error?: string
  help?: string
}

export function JosefSenderSelector({
  label = 'Send from',
  placeholder = 'Select a sender',
  value = '',
  emailAddresses = [],
  isLoading = false,
  onChange,
  onCreateSender,
  className,
  error,
  help
}: JosefSenderSelectorProps) {
  const dialogRef = useRef<NativeDialogHandle>(null)

  // Auto-select first address if none selected
  useEffect(() => {
    if (!value && emailAddresses.length > 0) {
      const firstAddress = emailAddresses[0]
      onChange(firstAddress.id, firstAddress)
    }
  }, [value, emailAddresses, onChange])

  const selectedAddress = emailAddresses.find(addr => addr.id === value)

  const handleCreateSender = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!onCreateSender) return

    const formData = new FormData(event.currentTarget)
    const name = formData.get('name') as string
    const email = formData.get('email') as string

    if (!name || !email) return

    try {
      const newAddress = await onCreateSender({ name, email })
      onChange(newAddress.id, newAddress)
      dialogRef.current?.close()

      // Reset form
      const form = event.currentTarget
      form.reset()
    } catch (error) {
      console.error('Failed to create sender:', error)
    }
  }

  // Determine status and help text
  const getStatusProps = () => {
    if (error) return { error, help }

    if (!selectedAddress) {
      return { help: 'Select the "from" address for this campaign' }
    }

    if (selectedAddress.verification_status !== 'Success') {
      return {
        error: 'This address has not been verified',
        help: 'Please verify this email address before using it'
      }
    }

    return { help }
  }

  const statusProps = getStatusProps()

  return (
    <>
      <NativeDialog
        ref={dialogRef}
        title="Add Email Sender"
        className="w-[500px] max-w-[90vw]"
      >
        <form onSubmit={handleCreateSender} className="space-y-6">
          <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              We need to verify that you have access to an email address at the
              domain you plan to send campaigns from. Enter an address, and
              we'll send you a verification email.{' '}
              <a
                className="underline hover:no-underline"
                href="https://help.stampede.ai/en/articles/5455199-custom-domains-within-stampede"
                target="_blank"
                rel="noopener"
              >
                Learn more
              </a>
              .
            </p>
          </div>

          <JosefInput
            name="name"
            label="Sender Name"
            placeholder="Enter sender name..."
            help="Enter the name emails will appear to come from"
            required
          />

          <JosefInput
            name="email"
            type="email"
            label="Email Address"
            placeholder="Enter email address..."
            help="Enter an email address you have access to"
            required
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-black/5 dark:border-white/5">
            <JosefButton
              variant="ghost"
              onClick={() => dialogRef.current?.close()}
            >
              Cancel
            </JosefButton>
            <JosefButton
              variant="primary"
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
            >
              Send Verification Email
            </JosefButton>
          </div>
        </form>
      </NativeDialog>

      <div className={cn('space-y-2', className)}>
        {label && (
          <label className="block text-[14px] font-medium text-gray-900 dark:text-white">
            {label}
          </label>
        )}

        <div className="flex gap-2">
          <div className="relative flex-1">
            <select
              value={value}
              onChange={(e) => {
                const selectedId = e.target.value
                const selectedAddr = emailAddresses.find(addr => addr.id === selectedId)
                onChange(selectedId, selectedAddr)
              }}
              disabled={emailAddresses.length === 0 || isLoading}
              className={cn(
                'w-full h-10 pl-3 pr-10 py-2 text-sm bg-white dark:bg-black border border-transparent rounded-lg text-black dark:text-white focus:outline-none focus:ring-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed appearance-none',
                statusProps.error && 'border-red-500 dark:border-red-400'
              )}
            >
              <option value="" disabled>
                {placeholder}
              </option>
              {emailAddresses.map((address) => (
                <option key={address.id} value={address.id}>
                  {address.name} &lt;{address.email}&gt;
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-3 top-3 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>

          {onCreateSender && (
            <JosefIconButton
              variant="ghost"
              icon={<PlusIcon className="h-4 w-4" />}
              onClick={() => dialogRef.current?.showModal()}
              disabled={isLoading}
              aria-label="Add new sender"
            />
          )}
        </div>

        {(statusProps.help || statusProps.error) && (
          <p className={cn(
            'text-[12px] mt-1',
            statusProps.error
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400'
          )}>
            {statusProps.error || statusProps.help}
          </p>
        )}
      </div>
    </>
  )
}